import { useState, useCallback, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import type { Business } from './useBusinesses';

export type EditBusinessInput = Partial<
  Pick<
    Business,
    | 'name'
    | 'email'
    | 'phone_number'
    | 'address'
    | 'location_lat'
    | 'location_lng'
    | 'timezone'
    | 'capacity'
    | 'business_logo'
    | 'business_type'
  > & {
    description: string;
    /** Canonical jsonb ARRAY shape, matching what businesses.hours stores.
     *  A record keyed by day is the FORM's representation, not the wire's. */
    hours: Array<{ day: string; open: string; close: string; isClosed: boolean }>;
    /** GeoJSON Polygon geometry; the API converts it to the stored shape.
     *  null removes the venue's geofence. */
    geofence: { type: string; coordinates: number[][][] } | null;
    tags: string[];
    cover_price: unknown;
    show_capacity: boolean;
    verified: boolean;
  }
>;

interface UseEditBusinessResult {
  loading: boolean;
  error: string | null;
  edit: (businessId: string, input: EditBusinessInput, apiUrl?: string) => Promise<boolean>;
}

export function useEditBusiness(): UseEditBusinessResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const edit = useCallback(async (businessId: string, input: EditBusinessInput, apiUrl?: string): Promise<boolean> => {
    const url = apiUrl || process.env.REACT_APP_API_URL;
    if (!url) { setError('REACT_APP_API_URL is not set'); return false; }

    setLoading(true);
    setError(null);

    try {
      let token: string | null = null;
      if (isAuthenticated && accountsRef.current.length > 0) {
        token = await acquireApiToken(instance, accountsRef.current[0]);
      }

      const response = await fetch(`${url}/admin/businesses/${businessId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(input),
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.error || `HTTP ${response.status}`);
        return false;
      }

      return true;
    } catch (err: any) {
      setError(err?.message ?? 'Unknown error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, instance]);

  return { loading, error, edit };
}
